import { useState } from 'react'
import { useLocale } from 'next-intl'
import { convertScientificNotation } from '@node-space/utils'
import { createLink, IntegrationAccessToken, LinkPayload } from '@meshconnect/web-link-sdk'
import { v4 as generateUuid } from 'uuid'
import {
  CreateMeshLinkTokenRequestBody,
  IntegrationBroker,
  MeshSavedLinkToken,
  MeshSettings,
} from '@/types/mesh'
import { Flow } from '@/types/payment'
import { WalletTransaction } from '@/types/wallet'
import { getFullLocale, logSentryError } from '@/utils/helpers'
import { redirectToExternalUrl } from '@/utils/utils'
import { useCreateLinkToken } from './mutations/useCreateLinkToken'
import { useSaveLinkToken } from './mutations/useSaveLinkToken'
import { useSavePaymentOriginator } from './mutations/useSavePaymentOriginator'
import { useVerifyPaymentOriginator } from './mutations/useVerifyPaymentOriginator'
import useTrackEvent from './useTrackEvent'

export const useMeshConnect = (transaction: WalletTransaction, meshSettings?: MeshSettings) => {
  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()

  const track = useTrackEvent()
  const locale = useLocale()
  const { mutate: createLinkToken } = useCreateLinkToken()
  const { mutate: saveLinkToken } = useSaveLinkToken()
  const { mutate: verifyPaymentOriginator } = useVerifyPaymentOriginator()
  const { mutate: savePaymentOriginator } = useSavePaymentOriginator()

  const fullLocale = getFullLocale(locale)

  const trackMeshEvent = (action: string, payload?: Record<string, unknown>) => {
    track('MeshConnect', {
      category: 'Hosted Payments Page',
      payment_id: transaction?.uuid,
      merchantName: transaction?.merchantName,
      reference: transaction?.reference,
      user_id: transaction?.userId,
      amount: convertScientificNotation(transaction?.amount || 0),
      flow: Flow.IN,
      version: 2,
      meshAction: action,
      meshActionPayload: payload,
    })
  }

  const handleVerifyPaymentOriginator = (
    accessToken: string,
    brokerName: string,
    transactionId: string
  ) => {
    if (meshSettings?.meshExchangeVerificationEnabled) {
      verifyPaymentOriginator({
        userId: transaction?.uuid,
        accessToken,
        transactionId,
        integrationBroker: brokerName as IntegrationBroker,
      })
    }
  }

  const handleSavePaymentOriginator = (
    transactionId: string,
    verifiedAddress: string,
    isVerified: boolean
  ) => {
    if (meshSettings?.meshWalletVerificationEnabled) {
      savePaymentOriginator({
        userId: transaction?.uuid,
        transactionId,
        verifiedAddress,
        isVerified,
      })
    }
  }

  const openLink = (
    linkToken: string,
    transactionId: string,
    savedLinkToken?: MeshSavedLinkToken
  ) => {
    if (savedLinkToken) {
      handleVerifyPaymentOriginator(
        savedLinkToken?.accessToken,
        savedLinkToken?.brokerName,
        transactionId
      )
    }

    const link = createLink({
      language: fullLocale,
      onIntegrationConnected: (payload: LinkPayload) => {
        saveLinkToken({
          userId: transaction?.uuid,
          transactionId,
          networkId: transaction?.addressOptions?.[0]?.networkId ?? '',
          accountId: payload?.accessToken?.accountTokens?.[0]?.account?.accountId ?? '',
          accountName: payload?.accessToken?.accountTokens?.[0]?.account?.accountName ?? '',
          accessToken: payload?.accessToken?.accountTokens?.[0]?.accessToken ?? '',
          refreshToken: payload?.accessToken?.accountTokens?.[0]?.refreshToken ?? '',
          expiresInSeconds: payload?.accessToken?.expiresInSeconds ?? null,
          refreshTokenExpiresInSeconds: payload?.accessToken?.refreshTokenExpiresInSeconds ?? null,
          brokerType: payload?.accessToken?.brokerType ?? '',
          brokerName: payload?.accessToken?.brokerName ?? '',
          brokerBrandInfo: {
            brokerLogoUrl: payload?.accessToken?.brokerBrandInfo.brokerLogoUrl ?? '',
            brokerLogo: payload?.accessToken?.brokerBrandInfo.brokerLogo ?? '',
          },
        })

        handleVerifyPaymentOriginator(
          payload?.accessToken?.accountTokens?.[0]?.accessToken ?? '',
          payload?.accessToken?.brokerName ?? '',
          transactionId
        )
      },
      onExit: (error, summary) => {
        if (error) {
          setError(`Error: ${error}`)
          logSentryError('Mesh Connect: link error', error, {
            userId: transaction?.userId,
            channelId: transaction?.uuid,
            paymentId: transaction?.paymentId || transaction?.uuid,
          })
        }

        if (summary?.page === 'transferExecutedPage' && transaction?.returnUrl) {
          redirectToExternalUrl(transaction.returnUrl)
        }
      },
      onEvent: ev => {
        if ('payload' in ev && ev.payload) {
          trackMeshEvent(ev.type, ev.payload as Record<string, unknown>)
        } else {
          trackMeshEvent(ev.type)
        }

        if (ev.type === 'walletMessageSigned') {
          handleSavePaymentOriginator(transactionId, ev.payload.address, ev.payload.isVerified)
        }
      },
      ...(savedLinkToken && {
        accessTokens: [
          {
            accountId: savedLinkToken?.accountId,
            accountName: savedLinkToken?.accountName,
            accessToken: savedLinkToken?.accessToken,
            brokerType: savedLinkToken?.brokerType,
            brokerName: savedLinkToken?.brokerName,
          } as IntegrationAccessToken,
        ],
      }),
    })

    link.openLink(linkToken)
  }

  const fetchLinkToken = (savedLinkToken?: MeshSavedLinkToken) => {
    const transactionId = savedLinkToken ? savedLinkToken.transactionId : generateUuid()

    const requestBody: CreateMeshLinkTokenRequestBody = {
      userId: transaction?.uuid,
      transactionId,
      addressOptions: transaction?.addressOptions || [],
      subClientId: process.env.NEXT_PUBLIC_BVNK_SUB_CLIENT_ID ?? '',
      verifyWallet: meshSettings?.meshWalletVerificationEnabled,
    }

    if (transaction?.addressTag) {
      requestBody.addressTag = transaction?.addressTag
    }

    setIsFetchingToken(true)
    createLinkToken(requestBody, {
      onError: (error: unknown) => {
        setError('Error getting link token')
        logSentryError('Mesh Connect: Error getting link token', error, {
          userId: transaction?.userId,
          paymentId: transaction?.uuid,
        })
      },
      onSuccess: data => openLink(data?.linkToken, transactionId, savedLinkToken),
      onSettled: () => setIsFetchingToken(false),
    })
  }

  return {
    openMeshLink: fetchLinkToken,
    isFetchingToken,
    error,
  }
}
